/**
 * Label/value row used inside card bodies.
 * Spec: v2-design/CARD_SHELL_v0_spec.md §10.
 *
 * The label sits on a fixed left column (~120px); the value flows to
 * the right. Long values wrap. Empty values render as a muted em-dash
 * so the row stays visually aligned with siblings.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

interface FieldProps {
  label: string;
  /** Value as a string OR a React node (for chip lists, links, etc.). */
  value: React.ReactNode;
  /** Optional muted secondary line below the value. */
  helper?: React.ReactNode;
}

export const Field: React.FC<FieldProps> = ({ label, value, helper }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.25 }}>
    <Typography
      component="span"
      sx={{
        fontSize: 12,
        fontWeight: 500,
        color: 'text.secondary',
        width: 120,
        flexShrink: 0,
        lineHeight: 1.5,
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        component="div"
        sx={{
          fontSize: 13,
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}
      >
        {isEmpty(value) ? (
          <Box component="span" sx={{ color: 'text.disabled' }}>
            —
          </Box>
        ) : (
          value
        )}
      </Typography>
      {helper ? (
        <Typography
          component="div"
          sx={{
            fontSize: 12,
            color: 'text.secondary',
            mt: 0.25,
          }}
        >
          {helper}
        </Typography>
      ) : null}
    </Box>
  </Box>
);

function isEmpty(value: React.ReactNode): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
}
